import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';

/**
 * The ONE "what's included" disclosure (2026-08-04) — used by the
 * add-property form and the booking request page, so the promise reads
 * identically everywhere. Linen figures mirror the office's default standard
 * quantities (StandardQuantityPopup); amenities are names only — quantities
 * scale with the apartment size.
 */
export const AMENITY_ITEMS = [
    'Bottled water', 'Teabags', 'Coffee sachets', 'Sugar sticks',
    'Toilet paper', 'Kitchen towel', 'Face tissues', 'Trash bags',
    'Laundry capsules', 'Dishwasher capsules',
];

/**
 * The standard linen set a layout gets with every All Inclusive cleaning —
 * the same per-bed rule the warehouse packs from (owner, 2026-09-14: the
 * add-property form shows the client THEIR numbers, not the generic text).
 *   Queen/King or Super King bed: 1 bedsheet · 1 duvet cover · 4 pillowcases ·
 *                                 2 bath · 2 hand · 2 face towels
 *   Single bed:                   1 bedsheet · 1 duvet cover · 2 pillowcases ·
 *                                 1 bath · 1 hand · 1 face towel
 *   + a bathmat for every bathroom (a half bathroom counts as one).
 */
export function standardLinenItems({ single_beds, double_beds, super_king_beds, bathrooms }) {
    const single = Math.max(0, Number(single_beds) || 0);
    const double = Math.max(0, Number(double_beds) || 0);
    const sk = Math.max(0, Number(super_king_beds) || 0);
    const baths = Math.ceil(Math.max(0, Number(bathrooms) || 0));
    const big = double + sk;
    const towels = single + big * 2;
    const items = [
        { name: 'Double bedsheet', count: double },
        { name: 'Super King bedsheet', count: sk },
        { name: 'Single bedsheet', count: single },
        { name: 'Double duvet cover', count: double },
        { name: 'Super King duvet cover', count: sk },
        { name: 'Single duvet cover', count: single },
        { name: 'Pillowcase', count: single * 2 + big * 4 },
        { name: 'Bath towel', count: towels },
        { name: 'Hand towel', count: towels },
        { name: 'Face towel', count: towels },
        { name: 'Bathmat', count: baths },
    ];
    return items.filter((it) => it.count > 0).map((it) => ({ ...it, name: it.count === 1 ? it.name : `${it.name}s` }));
}

export default function IncludedSet({
    label = '✓ Standard Linen Hire and Amenities always included',
    // { custom: bool, items: [{ name, count }] } — the property's ACTUAL set
    // (same property_sq rows warehouse duty packs from). When present, the
    // generic per-bed text gives way to the real numbers.
    quantities = null,
    // Heading + hint over the numbers; the booking page keeps the defaults.
    quantitiesTitle = 'Linen set for this property',
    quantitiesHint = 'You can request additional items below.',
}) {
    const [open, setOpen] = useState(false);
    const hasQuantities = !!(quantities && Array.isArray(quantities.items) && quantities.items.length);
    return (
        <>
            <button
                type="button"
                className="creq-linen-note creq-linen-note--toggle"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
            >
                <span>{label}</span>
                <FontAwesomeIcon
                    icon={faChevronDown}
                    className={`creq-linen-chevron ${open ? 'is-open' : ''}`}
                />
            </button>
            {open && (
                <div className="creq-linen-detail">
                    {hasQuantities ? (
                        <div className="creq-linen-detail-col creq-linen-detail-col--wide">
                            <strong>
                                {quantitiesTitle}
                                {quantities.custom ? ' · customised for you' : ''}
                            </strong>
                            <div className="creq-amenity-chips">
                                {quantities.items.map((it) => (
                                    <span key={it.name} className="creq-amenity-chip creq-amenity-chip--count">
                                        {it.count}× {it.name}
                                    </span>
                                ))}
                            </div>
                            <span className="creq-linen-detail-hint">
                                {quantitiesHint}
                            </span>
                        </div>
                    ) : (
                        <>
                            <div className="creq-linen-detail-col">
                                <strong>Per Queen / King / Super King Size bed</strong>
                                <span>1 bedsheet · 1 duvet cover · 4 pillowcases</span>
                                <span>2 bath towels · 2 hand towels · 2 face towels</span>
                            </div>
                            <div className="creq-linen-detail-col">
                                <strong>Per single bed</strong>
                                <span>1 bedsheet · 1 duvet cover · 2 pillowcases</span>
                                <span>1 bath towel · 1 hand towel · 1 face towel</span>
                            </div>
                            <div className="creq-linen-detail-foot">
                                + a bathmat for every bathroom, with every All&nbsp;Inclusive cleaning.
                            </div>
                        </>
                    )}
                    <div className="creq-linen-detail-col creq-linen-detail-col--wide">
                        <strong>Welcome pack &amp; amenities</strong>
                        <div className="creq-amenity-chips">
                            {AMENITY_ITEMS.map((item) => (
                                <span key={item} className="creq-amenity-chip">{item}</span>
                            ))}
                        </div>
                        <span className="creq-linen-detail-hint">
                            Quantities scale with the size of your apartment.
                        </span>
                    </div>
                </div>
            )}
        </>
    );
}
